// Ops matrix for the Dashboard: what the Engine routes say, and what the screen may show from them.
// Every value is a Cell: ready (a real answer), notConnected (no route gives it) or unavailable
// (the route failed or answered something invalid). A number is only ever shown from a ready Cell.

export const READY = 'ready'
export const NOT_CONNECTED = 'notConnected'
export const UNAVAILABLE = 'unavailable'

export const ready = value => ({ kind: READY, value })
export const notConnected = needs => ({ kind: NOT_CONNECTED, needs })
export const unavailable = reason => ({ kind: UNAVAILABLE, reason })

// What each value without a source would need. A new backend route is a separate card.
export const NEEDS = Object.freeze({
  research: 'A per-recipient research status on the business campaign record (it carries evidence sources only).',
  sent: 'The sends and replies per day route is not answering, so nothing counts sends.',
  sentToday: 'A sent-today total route. Same counter as the Sent column.',
  apiErrors: 'An Engine route that reports its own HTTP error count for the last 24 hours (today only Cloudflare analytics has it, and the browser cannot call that).',
  deliverability: 'A placement-check and unsubscribe route. Mailbox rows carry a deliverability field that always reads unknown.',
  candidateList: 'A list route for outreach (candidate) campaigns. Only POST and GET by id exist, so this row has no figures.',
  noMailbox: 'A sender mailbox on the campaign. This campaign has none set.',
  series: 'The sends and replies per day route (GET /api/business-campaigns/delivery/daily) is not answering.',
  funnelSent: 'The sent and total-reply counters. Held replies are only the replies Engine could not match.',
  cost: 'A spend and cost-per-reply route for owners and operators. Nothing meters spend by campaign yet.',
  placement: 'Inbox placement checks per mailbox. Saved warmup observations are advisory and are not read on this screen yet.',
})

const FAIL_STATUS = new Set([404, 503])
export const stateOfError = e => (FAIL_STATUS.has(e?.status) ? NOT_CONNECTED : UNAVAILABLE)

async function settle(run) {
  try { return { state: READY, data: await run() } } catch (e) {
    if (e?.name === 'AbortError') throw e
    return { state: stateOfError(e), data: null }
  }
}

// One source gets one deadline for all of its requests together. Each client times a single request out at 15 seconds,
// and a source asks several in turn (a list, then a record or a status per row, four at a time), so without a deadline
// the wait grew by 15 seconds per batch of rows. At the deadline the source reads unavailable and its requests are aborted.
export const SOURCE_DEADLINE_MS = 8000

async function bounded(run, deadlineMs, outer) {
  const controller = new AbortController()
  const relay = () => controller.abort()
  if (outer?.aborted) controller.abort()
  outer?.addEventListener('abort', relay, { once: true })
  let timer
  const late = new Promise(resolve => { timer = setTimeout(() => { controller.abort(); resolve({ state: UNAVAILABLE, data: null, timedOut: true }) }, deadlineMs) })
  const cancelled = new Promise((_, reject) => {
    const stop = () => reject(new DOMException('Cancelled', 'AbortError'))
    if (outer?.aborted) stop()
    else outer?.addEventListener('abort', stop, { once: true })
  })
  try {
    return await Promise.race([settle(() => run(controller.signal)), late, cancelled])
  } finally {
    clearTimeout(timer)
    outer?.removeEventListener('abort', relay)
  }
}

async function pool(items, size, task) {
  const out = new Array(items.length)
  let next = 0
  const worker = async () => { for (;;) { const i = next++; if (i >= items.length) return; out[i] = await task(items[i], i) } }
  await Promise.all(Array.from({ length: Math.min(size, items.length) }, worker))
  return out
}

const NONE = { state: NOT_CONNECTED, data: null }

// The clients may be null (the service is not configured), which reads as not connected.
export async function loadOpsSources({ business, replies, mailboxes, sends, signal, deadlineMs = SOURCE_DEADLINE_MS } = {}) {
  const within = run => bounded(run, deadlineMs, signal)
  const [campaigns, held, boxes, series] = await Promise.all([
    business ? within(async signal => {
      const list = await business.list({ signal })
      const records = Object.create(null)
      const loaded = await pool(list.campaigns, 4, c => settle(() => business.load(c.id, { signal })))
      list.campaigns.forEach((c, i) => { records[c.id] = loaded[i] })
      return { list: list.campaigns, records }
    }) : NONE,
    replies ? within(signal => replies.list({ signal })) : NONE,
    mailboxes ? within(async signal => {
      const list = await mailboxes.list({ signal })
      const answers = await pool(list.mailboxes, 4, row => settle(() => mailboxes.replyStatus(row, { signal })))
      const bad = answers.find(a => a.state !== READY)
      const times = answers.map(a => a.data?.replyStatus?.lastAuthenticatedReceiptAt).filter(Number.isFinite)
      return { rows: list.mailboxes, receipt: bad ? { state: bad.state } : { state: READY, at: times.length ? Math.max(...times) : null } }
    }) : NONE,
    sends ? within(signal => sends.series({ signal })) : NONE,
  ])
  return { campaigns, held, boxes, series }
}

const cellOf = (source, pick) => (source.state === READY ? ready(pick(source.data)) : source.state === NOT_CONNECTED ? notConnected('The service is not connected.') : unavailable('The route failed.'))

// Drafts with no human review of the current draft revision.
export function draftsWaiting(record) {
  const reviewed = new Set(record.reviews.map(r => `${r.recipientId}:${r.draftRevision}`))
  return record.drafts.filter(d => !reviewed.has(`${d.recipientId}:${d.revision}`)).length
}

// Recipients whose newest review accepted the draft that is current now.
export function approvedCount(record) {
  const newest = new Map()
  for (const r of record.reviews) if (!newest.has(r.recipientId) || newest.get(r.recipientId).reviewSequence < r.reviewSequence) newest.set(r.recipientId, r)
  const current = new Map(record.drafts.map(d => [d.recipientId, d.revision]))
  return [...newest.values()].filter(r => r.decision === 'accepted' && current.get(r.recipientId) === r.draftRevision).length
}

const needsAttention = row => ['held', 'stale'].includes(row.connection.status) || row.policy.paused || row.policy.quarantined || row.policy.suppressed

const DAY = /^\d{4}-\d{2}-\d{2}$/
const count = n => Number.isSafeInteger(n) && n >= 0
const countMap = (m, ids) => m !== null && typeof m === 'object' && !Array.isArray(m) && Object.entries(m).every(([id, n]) => ids.has(id) && count(n))
const realDay = d => typeof d === 'string' && DAY.test(d) && (t => Number.isFinite(t) && new Date(t).toISOString().slice(0, 10) === d)(Date.parse(`${d}T00:00:00Z`))
const MAX_SPAN = 90

// A campaign on a day whose sends are not proven: some attempt may have reached the provider with no verdict.
export const isUnproven = (day, id) => (day.unknown?.[id] ?? 0) > 0

const datesBetween = (from, to) => {
  const out = []
  for (let t = Date.parse(`${from}T00:00:00Z`), end = Date.parse(`${to}T00:00:00Z`); t <= end; t += 86400000) out.push(new Date(t).toISOString().slice(0, 10))
  return out
}

// A sends and replies series is used whole or not at all when it is malformed: a bad day, a negative count or a
// campaign the list does not hold makes every total in it untrustworthy, so the answer is null and the screen says
// Unavailable. A count that is simply absent is a gap, kept as null and never turned into a zero. A campaign with
// unknown sends on a day keeps that count apart (never summed across days) and the day reads not proven.
// A range with no days at all is an empty ready series. When days exist, the days of the range the route left out
// are added as gaps, so the axis is one tick per Chicago day.
export function normalizeSeries(raw, ids) {
  const known = new Set(ids)
  if (!raw || !Array.isArray(raw.days) || raw.days.length > 366) return null
  const held = raw.held ?? {}
  if (held === null || typeof held !== 'object' || Array.isArray(held) || !Object.entries(held).every(([d, n]) => realDay(d) && count(n))) return null
  let range = null
  if (raw.range !== undefined) {
    const r = raw.range
    if (!r || !realDay(r.from) || !realDay(r.to) || r.from > r.to || datesBetween(r.from, r.to).length > MAX_SPAN) return null
    range = { from: r.from, to: r.to }
  }
  let last = ''
  for (const d of raw.days) {
    if (!d || !realDay(d.date) || d.date <= last) return null
    if (!countMap(d.sent, known) || !countMap(d.replies, known)) return null
    if (!countMap(d.unknown, known) || !Object.entries(d.unknown).every(([id, n]) => n > 0 && Object.hasOwn(d.sent, id))) return null
    if (range && (d.date < range.from || d.date > range.to)) return null
    last = d.date
  }
  const full = m => Object.fromEntries(ids.map(id => [id, Object.hasOwn(m, id) ? m[id] : null]))
  const unknownOf = m => Object.fromEntries(ids.map(id => [id, m && Object.hasOwn(m, id) ? m[id] : 0]))
  const byDate = new Map(raw.days.map(d => [d.date, d]))
  const dates = range && raw.days.length ? datesBetween(range.from, range.to) : raw.days.map(d => d.date)
  const days = dates.map(date => { const d = byDate.get(date) ?? { sent: {}, replies: {} }; return { date, sent: full(d.sent), replies: full(d.replies), unknown: unknownOf(d.unknown) } })
  return { days, held: { ...held }, span: range ? datesBetween(range.from, range.to).length : days.length, workspaceId: raw.workspaceId }
}

// What the sends cells show over the whole series for a set of campaigns. Sent counts only confirmed sends: a day with
// unknown sends keeps the sends already confirmed that day and adds nothing for the rest, and it is counted so the
// screen can say so. A campaign with no count at all on a day (no sent entry, or no replies entry) is a gap, counted in
// gapDays and never read as a zero. The rate is matched replies over confirmed sends, and it is null when there are no
// confirmed sends or when any gap leaves the numerator or the denominator incomplete.
export function summarizeSends(series, ids) {
  let sent = 0, replies = 0, unprovenDays = 0, gapDays = 0
  for (const d of series.days) {
    let unproven = false, gap = false
    for (const id of ids) {
      const unknown = isUnproven(d, id)
      if (d.sent[id] === null && !unknown) gap = true
      if (d.replies[id] === null) gap = true
      sent += d.sent[id] ?? 0
      replies += d.replies[id] ?? 0
      if (unknown) unproven = true
    }
    if (unproven) unprovenDays++
    if (gap) gapDays++
  }
  return { sent, replies, rate: gapDays === 0 && sent > 0 ? replies / sent : null, unprovenDays, gapDays, span: series.span, recorded: series.days.length > 0, workspaceId: series.workspaceId }
}

// One point per day for the cover sparkline: confirmed sends, null on a day not proven and on a day where any selected
// campaign has no count (a partial sum is never drawn as the aggregate), and the days that are not proven.
export function sparkOf(series, ids) {
  return series.days.map(d => {
    const unproven = ids.some(id => isUnproven(d, id))
    const complete = ids.every(id => d.sent[id] !== null)
    return { date: d.date, value: unproven || !complete || ids.length === 0 ? null : ids.reduce((n, id) => n + d.sent[id], 0), unproven }
  })
}

const sameAddress = (a, b) => typeof a === 'string' && typeof b === 'string' && a.toLowerCase() === b.toLowerCase()

function mailboxCell(sender, boxes) {
  if (!sender) return { kind: NOT_CONNECTED, needs: NEEDS.noMailbox }
  const base = { kind: READY, address: sender.address }
  if (boxes.state !== READY) return { ...base, tone: 'unknown', word: boxes.state === NOT_CONNECTED ? 'status not connected' : 'status unavailable' }
  const row = boxes.data.rows.find(r => r.mailboxId === sender.mailboxId)
  if (!row) return { ...base, tone: 'unknown', word: 'not in the mailbox list' }
  if (needsAttention(row)) return { ...base, tone: 'warning', word: 'needs attention' }
  if (row.connection.status === 'passed') return { ...base, tone: 'success', word: 'connection passed' }
  return { ...base, tone: 'neutral', word: 'not checked yet' }
}

function repliesCell(sender, held) {
  if (held.state !== READY) return cellOf(held, () => 0)
  if (!sender) return notConnected(NEEDS.noMailbox)
  if (held.data.hasMore) return unavailable('More held replies than one page, so a per-mailbox count would be partial.')
  return ready(held.data.events.filter(e => e.mailbox && sameAddress(e.mailbox.address, sender.address)).length)
}

function businessRow(summary, source, held, boxes) {
  const record = source?.state === READY ? source.data : null
  const fromRecord = pick => (record ? ready(pick(record)) : source?.state === NOT_CONNECTED ? notConnected('The campaign record is not connected.') : unavailable('The campaign record did not load.'))
  const sender = record ? record.campaign.sender : null
  return {
    key: summary.id,
    kind: 'business',
    tone: summary.state === 'active' ? 'success' : 'warning',
    name: summary.id,
    detail: `${summary.clientId || summary.subjectRef || 'No client'}, ${summary.state}`,
    leads: ready(summary.recipientCount),
    research: notConnected(NEEDS.research),
    drafts: fromRecord(r => r.drafts.length),
    waiting: fromRecord(draftsWaiting),
    approved: fromRecord(approvedCount),
    queued: fromRecord(r => r.queue.length),
    sent: notConnected(NEEDS.sent),
    replies: record ? repliesCell(sender, held) : fromRecord(() => 0),
    mailbox: record ? mailboxCell(sender, boxes) : { kind: UNAVAILABLE },
  }
}

const MANUAL_ROW = Object.freeze({
  key: 'melissa-job-outreach', kind: 'manual', tone: 'success', name: 'Melissa job outreach', detail: 'Clairvos, manual',
  leads: notConnected(NEEDS.candidateList), research: notConnected(NEEDS.candidateList), drafts: notConnected(NEEDS.candidateList), waiting: notConnected(NEEDS.candidateList),
  approved: notConnected(NEEDS.candidateList), sent: { kind: 'manual', text: 'she sends' }, replies: notConnected(NEEDS.candidateList),
  mailbox: { kind: 'manual', text: 'her own mail' },
})

export function buildOpsModel({ campaigns, held, boxes, series = NONE }) {
  const listed = campaigns.state === READY
  const ids = listed ? campaigns.data.list.map(c => c.id) : []
  let seriesCell
  if (series.state === READY) {
    const norm = normalizeSeries(series.data, ids)
    seriesCell = norm ? ready(norm) : unavailable('The series did not match the campaign list.')
  } else seriesCell = series.state === NOT_CONNECTED ? notConnected(NEEDS.series) : unavailable('The series route failed.')

  // The figures a series gives a set of campaigns, or the reason it gives none.
  const fromSeries = of => {
    if (seriesCell.kind !== READY) return { sent: seriesCell, rate: seriesCell, spark: seriesCell }
    const sums = ready(summarizeSends(seriesCell.value, of))
    return { sent: sums, rate: sums, spark: ready(sparkOf(seriesCell.value, of)) }
  }
  const rows = listed ? campaigns.data.list.map(c => ({ ...businessRow(c, campaigns.data.records[c.id], held, boxes), ...fromSeries([c.id]) })) : []
  const records = listed ? campaigns.data.list.map(c => campaigns.data.records[c.id]) : []
  const allLoaded = listed && records.every(r => r.state === READY)
  const sum = pick => (listed ? (allLoaded ? ready(records.reduce((n, r) => n + pick(r.data), 0)) : unavailable('A campaign record did not load, so a total would be partial.')) : cellOf(campaigns, () => 0))

  const active = listed ? campaigns.data.list.filter(c => c.state === 'active').length : 0
  const paused = listed ? campaigns.data.list.filter(c => c.state === 'paused').length : 0

  let system
  if (boxes.state !== READY) system = boxes.state === NOT_CONNECTED ? notConnected('Mailbox health is not connected.') : unavailable('Mailbox health did not load.')
  else {
    const attention = boxes.data.rows.filter(needsAttention).length
    system = ready({ tone: attention ? 'warning' : 'success', text: attention ? `${attention} ${attention === 1 ? 'mailbox needs' : 'mailboxes need'} attention` : 'Mailboxes OK' })
  }

  const recipients = listed ? ready(campaigns.data.list.reduce((n, c) => n + c.recipientCount, 0)) : cellOf(campaigns, () => 0)
  const inboxes = boxes.state === READY
    ? ready(boxes.data.rows.map(r => ({ mailboxId: r.mailboxId, address: r.address, provider: r.provider, ...(needsAttention(r) ? { tone: 'warning', word: 'needs attention' } : r.connection.status === 'passed' ? { tone: 'success', word: 'connection passed' } : { tone: 'neutral', word: 'not checked yet' }) })))
    : cellOf(boxes, () => [])

  return {
    campaigns: rows.map(r => ({ ...r, id: r.key, state: campaigns.data.list.find(c => c.id === r.key).state })),
    all: {
      recipients,
      drafts: sum(draftsWaiting),
      drafted: sum(r => r.drafts.length),
      approved: sum(approvedCount),
      queued: sum(r => r.queue.length),
      ...fromSeries(ids),
      held: cellOf(held, d => d.summary.held),
    },
    series: seriesCell,
    inboxes,
    inboxPlacement: notConnected(NEEDS.placement),
    cost: notConnected(NEEDS.cost),
    headline: {
      replies: cellOf(held, d => d.summary.held),
      drafts: sum(draftsWaiting),
      campaigns: listed ? ready({ active, total: campaigns.data.list.length, paused }) : cellOf(campaigns, () => 0),
      sentToday: notConnected(NEEDS.sentToday),
      system,
    },
    rows: [...rows, MANUAL_ROW],
    listState: campaigns.state,
    machine: {
      apiErrors: notConnected(NEEDS.apiErrors),
      webhook: boxes.state !== READY ? cellOf(boxes, () => 0) : boxes.data.receipt.state === READY ? ready({ at: boxes.data.receipt.at }) : boxes.data.receipt.state === NOT_CONNECTED ? notConnected('Reply status is not connected.') : unavailable('Reply status did not load.'),
      queue: sum(r => r.queue.length),
      deliverability: notConnected(NEEDS.deliverability),
    },
  }
}
